import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Keeps Best Sellers navigation at a one-product step while delegating
 * scrolling, dragging, visibility, and reduced-motion behavior to Horizon.
 */
class UlifBestSellersCarousel extends Component {
  requiredRefs = ['slideshow', 'controls', 'current', 'total', 'previous', 'next'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #syncFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#teardown();

    this.#abortController = new AbortController();
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, {
      signal: this.#abortController.signal,
    });

    this.#observeSlideshow();
    this.#scheduleSync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeSlideshow();
    this.#scheduleSync();
  }

  disconnectedCallback() {
    this.#teardown();
    super.disconnectedCallback();
  }

  /** @param {Event} event */
  previous(event) {
    const state = this.#getState();
    if (!state) return;

    const target = Math.max(0, state.current - 1);
    if (target === state.current) {
      this.#renderState(state);
      return;
    }

    state.slideshow.select(target, event);
  }

  /** @param {Event} event */
  next(event) {
    const state = this.#getState();
    if (!state) return;

    const target = Math.min(state.lastStart, state.current + 1);
    if (target === state.current) {
      this.#renderState(state);
      return;
    }

    state.slideshow.select(target, event);
  }

  #observeSlideshow() {
    this.#resizeObserver?.disconnect();

    const slideshow = this.refs.slideshow;
    if (!(slideshow instanceof HTMLElement)) return;

    const container = slideshow.querySelector('slideshow-container');
    if (!(container instanceof HTMLElement)) return;

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleSync());
    this.#resizeObserver.observe(container);
  }

  #handleSlideshowSelect = (event) => {
    if (event.target !== this.refs.slideshow) return;
    this.#scheduleSync();
  };

  #scheduleSync() {
    if (this.#syncFrame !== undefined) cancelAnimationFrame(this.#syncFrame);

    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = undefined;
      if (this.isConnected) this.#sync();
    });
  }

  #sync() {
    const state = this.#getState();
    if (!state) return;

    if (state.current > state.lastStart) {
      const selection = state.slideshow.select(state.lastStart, undefined, { animate: false });
      Promise.resolve(selection).then(() => {
        if (!this.isConnected) return;
        const nextState = this.#getState();
        if (nextState) this.#renderState(nextState);
      });
      return;
    }

    this.#renderState(state);
  }

  #getState() {
    const slideshow = this.refs.slideshow;
    if (!(slideshow instanceof HTMLElement) || typeof slideshow.select !== 'function') return null;

    const slides = Array.isArray(slideshow.slides) ? slideshow.slides : [];
    if (slides.length === 0) return null;

    const visibleSlides = Array.isArray(slideshow.visibleSlides) ? slideshow.visibleSlides : [];
    const visibleCount = Math.max(1, visibleSlides.length);
    const lastStart = Math.max(0, slides.length - visibleCount);
    const rawCurrent = Number(slideshow.current);
    const current = Number.isInteger(rawCurrent) ? Math.min(Math.max(rawCurrent, 0), slides.length - 1) : 0;

    return { slideshow, slides, visibleCount, current, lastStart, total: slides.length };
  }

  #renderState(state) {
    const currentText = String(Math.min(state.total, state.current + state.visibleCount));
    const totalText = String(state.total);

    if (this.refs.current.textContent !== currentText) this.refs.current.textContent = currentText;
    if (this.refs.total.textContent !== totalText) this.refs.total.textContent = totalText;

    this.refs.previous.disabled = state.current <= 0;
    this.refs.next.disabled = state.current >= state.lastStart;
    this.refs.controls.hidden = state.lastStart === 0;

    state.slides.forEach((slide, index) => {
      const visible = index >= state.current && index < state.current + state.visibleCount;
      slide.setAttribute('aria-hidden', String(!visible));
      slide.toggleAttribute('inert', !visible);
      if ('inert' in slide) slide.inert = !visible;
    });

    this.toggleAttribute('data-ready', true);
  }

  #teardown() {
    const slideshow = this.refs.slideshow;
    const slides = slideshow instanceof HTMLElement && Array.isArray(slideshow.slides) ? slideshow.slides : [];
    slides.forEach((slide) => {
      slide.setAttribute('aria-hidden', 'false');
      slide.removeAttribute('inert');
      if ('inert' in slide) slide.inert = false;
    });

    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    if (this.#syncFrame !== undefined) cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = undefined;
    this.removeAttribute('data-ready');
  }
}

customElements.whenDefined('slideshow-component').then(() => {
  if (!customElements.get('ulif-best-sellers-carousel')) {
    customElements.define('ulif-best-sellers-carousel', UlifBestSellersCarousel);
  }
});
